
import React, { useState } from 'react';
import { BarChart3, Activity, Users, TrendingUp, Map, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, LineChart, Legend } from 'recharts';

const CampaignPerformance = () => {
  const [activeView, setActiveView] = useState('overview');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  // Sample data for campaign performance
  const campaignPerformanceData = {
    volumeData: [
      { name: 'May', actual: 190800, target: 200000 },
      { name: 'Jun', actual: 205400, target: 200000 },
      { name: 'Jul', actual: 220300, target: 200000 },
      { name: 'Aug', actual: 230800, target: 208000 },
    ],
    segmentImpact: [
      { name: 'Urban', value: 12.5, stations: 18 },
      { name: 'Highway', value: 9.3, stations: 24 },
      { name: 'Suburban', value: 3.8, stations: 15 },
      { name: 'Rural', value: -1.4, stations: 8 },
    ],
    stationPerformance: {
      top: [
        { name: 'Bangkok Central', segment: 'Urban', volume: 9850, change: 12.5 },
        { name: 'Pattaya Highway', segment: 'Highway', volume: 8720, change: 9.3 },
        { name: 'Sukhumvit 55', segment: 'Urban', volume: 7650, change: 8.7 },
        { name: 'Chiang Mai West', segment: 'Suburban', volume: 6940, change: 7.2 },
        { name: 'Highway 7 Exit 5', segment: 'Highway', volume: 6580, change: 6.8 },
      ],
      underperforming: [
        { name: 'Korat East', segment: 'Rural', volume: 2450, change: -3.2 },
        { name: 'Phuket South', segment: 'Suburban', volume: 2780, change: -2.8 },
        { name: 'Ayutthaya North', segment: 'Rural', volume: 3120, change: -1.5 },
        { name: 'Bangkok Outer Ring', segment: 'Urban', volume: 3580, change: -1.2 },
        { name: 'Chonburi Central', segment: 'Suburban', volume: 3650, change: -0.7 },
      ]
    }
  };

  return (
    <div className="p-6">
      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-1">Total Volume</div>
          <div className="flex items-end">
            <h3 className="text-3xl font-semibold text-gray-900">230,800 L</h3>
            <span className="ml-2 text-sm font-medium text-green-600 flex items-center">
              <ArrowUpRight size={14} className="mr-0.5" /> +14.8% vs. pre-campaign
            </span>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-1">Performance vs Target</div>
          <div className="flex items-end">
            <h3 className="text-3xl font-semibold text-gray-900">111.0%</h3>
            <span className="ml-2 text-sm font-medium text-blue-600">22,800 L over target</span>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-1">Participating Stations</div>
          <div className="flex items-end">
            <h3 className="text-3xl font-semibold text-gray-900">42</h3>
            <span className="ml-2 text-sm text-gray-500">Out of 65 eligible stations</span>
          </div>
        </div>
      </div>
      
      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveView('overview')}
            className={`px-6 py-3 text-sm font-medium ${activeView === 'overview' ? 'text-purple-700 border-b-2 border-purple-700' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Performance Overview
          </button>
          <button
            onClick={() => setActiveView('segments')}
            className={`px-6 py-3 text-sm font-medium ${activeView === 'segments' ? 'text-purple-700 border-b-2 border-purple-700' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Segment Analysis
          </button>
          <button
            onClick={() => setActiveView('stations')}
            className={`px-6 py-3 text-sm font-medium ${activeView === 'stations' ? 'text-purple-700 border-b-2 border-purple-700' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Station Performance
          </button>
        </div>
        
        {/* Performance Overview */}
        {activeView === 'overview' && (
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Campaign Volume vs Target</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={campaignPerformanceData.volumeData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="actual" name="Actual Volume" fill="#8B5CF6" />
                    <Bar dataKey="target" name="Target Volume" fill="#E0E0E0" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Geographic Performance Distribution</h3>
              <div className="text-sm text-gray-500 mb-4">Performance by region</div>
              <div className="h-64 bg-gray-50 rounded-md border border-dashed border-gray-300 flex items-center justify-center">
                <div className="text-center">
                  <Map size={40} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500">Geographic Map Visualization Placeholder</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Segment Analysis */}
        {activeView === 'segments' && (
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Segment Impact Analysis</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={campaignPerformanceData.segmentImpact}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[-5, 'dataMax + 2']} />
                    <YAxis dataKey="name" type="category" width={100} />
                    <Tooltip />
                    <Bar 
                      dataKey="value" 
                      name="Impact (%)" 
                      fill={(entry) => entry.value >= 0 ? '#8B5CF6' : '#EF4444'} 
                      radius={[0, 4, 4, 0]} 
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Segment Performance</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-4">
                    {campaignPerformanceData.segmentImpact.map((segment, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div>
                          <span className="font-medium text-gray-900">{segment.name}</span>
                          <div className="text-xs text-gray-500">{segment.stations} stations</div>
                        </div>
                        <div className={`text-sm font-medium ${segment.value >= 0 ? 'text-green-600' : 'text-red-600'} flex items-center`}>
                          {segment.value >= 0 ? 
                            <ArrowUpRight size={14} className="mr-0.5" /> : 
                            <ArrowDownRight size={14} className="mr-0.5" />
                          }
                          {segment.value >= 0 ? '+' : ''}{segment.value}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Optimization Opportunities</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                        <TrendingUp size={16} className="text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Increase Rural Coverage</h4>
                        <p className="text-sm text-gray-600">Adding 5 more rural stations could improve overall performance by estimated 1.8%</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                        <Users size={16} className="text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Target Suburban Customers</h4>
                        <p className="text-sm text-gray-600">Suburban segment showing 3.8% growth but has potential for 6-7% with targeted messaging</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                        <Activity size={16} className="text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Extend Campaign Duration</h4>
                        <p className="text-sm text-gray-600">Performance trend indicates potential for additional 5.2% growth with 2-week extension</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Station Performance */}
        {activeView === 'stations' && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Top Performing Stations</h3>
                <div className="text-sm text-gray-500 mb-2">Highest volume increase</div>
                
                <div className="space-y-4">
                  {campaignPerformanceData.stationPerformance.top.map((station, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{station.name}</div>
                        <div className="text-xs text-gray-500">{station.segment}</div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="font-medium text-gray-900">{station.volume.toLocaleString()} L</div>
                          <div className="text-xs text-green-600 flex items-center justify-end">
                            <ArrowUpRight size={14} className="mr-0.5" />
                            +{station.change}%
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Underperforming Stations</h3>
                <div className="text-sm text-gray-500 mb-2">Requires attention</div>
                
                <div className="space-y-4">
                  {campaignPerformanceData.stationPerformance.underperforming.map((station, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{station.name}</div>
                        <div className="text-xs text-gray-500">{station.segment}</div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="font-medium text-gray-900">{station.volume.toLocaleString()} L</div>
                          <div className="text-xs text-red-600 flex items-center justify-end">
                            <ArrowDownRight size={14} className="mr-0.5" />
                            {station.change}%
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Improvement Actions</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button className="border border-gray-200 rounded-md p-4 bg-white hover:bg-gray-50 text-left">
                  <h4 className="font-medium text-gray-900 mb-1">Adjust Pricing</h4>
                  <p className="text-sm text-gray-600">Modify pricing strategy for underperforming stations</p>
                </button>
                
                <button className="border border-gray-200 rounded-md p-4 bg-white hover:bg-gray-50 text-left">
                  <h4 className="font-medium text-gray-900 mb-1">Campaign Extension</h4>
                  <p className="text-sm text-gray-600">Extend campaign duration for specific segments</p>
                </button>
                
                <button className="border border-gray-200 rounded-md p-4 bg-white hover:bg-gray-50 text-left">
                  <h4 className="font-medium text-gray-900 mb-1">Local Marketing</h4>
                  <p className="text-sm text-gray-600">Increase local marketing efforts for specific stations</p>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Action Buttons */}
      <div className="flex justify-end space-x-4">
        <button className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50">
          Export Report
        </button>
        <button className="px-4 py-2 bg-purple-700 text-white rounded-md hover:bg-purple-800">
          Update Campaign
        </button>
      </div>
    </div>
  );
};

export default CampaignPerformance;
